import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Card,
    Button,
    Spinner,
    Alert,
    Modal,
    Form,
    ListGroup,
    Badge,
    Image,
} from 'react-bootstrap';

import { useOrganizationApi } from '@/shared/services/organizationApi';
import { useAdminApi } from '@/shared/services/adminApi';

// Default avatar placeholder - inline SVG data URI for user icon
const DEFAULT_AVATAR =
    'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="40" height="40" ' +
    'viewBox="0 0 40 40"%3E%3Ccircle cx="20" cy="20" r="20" fill="%23e9ecef"/%3E' +
    '%3Ccircle cx="20" cy="15" r="7" fill="%23adb5bd"/%3E' +
    '%3Cellipse cx="20" cy="32" rx="12" ry="9" fill="%23adb5bd"/%3E%3C/svg%3E';

/**
 * ManageManagers: the organization's access-control list. Owners and admins
 * grant and revoke management here. People merely connected to the org are not
 * listed: that is an affiliation, declared by the person, not granted here.
 */
export default function ManageManagers({
    organizationId,
    currentUserId,
    currentUserPermissionRole,
    isSystemAdmin,
    onSelfRemoved,
}) {
    const [managers, setManagers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Add manager modal state
    const [showAddModal, setShowAddModal] = useState(false);
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [userSearchResults, setUserSearchResults] = useState([]);
    const [searchingUsers, setSearchingUsers] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [newManagerRole, setNewManagerRole] = useState('admin');
    const [submitting, setSubmitting] = useState(false);

    // Remove confirmation
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [managerToRemove, setManagerToRemove] = useState(null);

    const orgApi = useOrganizationApi();
    const adminApi = useAdminApi();

    // Check if current user is an owner
    const isOwner = currentUserPermissionRole === 'owner' || isSystemAdmin;

    // Load managers
    // Reports whether the read was FORBIDDEN, which is different from failing:
    // a 403 is the server saying this viewer may no longer see the list at all,
    // and the self-removal path below acts on that rather than guessing.
    const loadManagers = useCallback(async () => {
        try {
            setLoading(true);
            const data = await orgApi.fetchManagers(organizationId);
            setManagers(data.managers || []);
            return { forbidden: false };
        } catch (err) {
            const forbidden = err.response?.status === 403;
            if (!forbidden) {
                setError('Failed to load managers');
                console.error(err);
            }
            return { forbidden };
        } finally {
            setLoading(false);
        }
    }, [organizationId, orgApi]);

    useEffect(() => {
        loadManagers();
    }, [loadManagers]);

    // User search with debounce
    useEffect(() => {
        if (!userSearchQuery || userSearchQuery.length < 2) {
            setUserSearchResults([]);
            return;
        }

        const searchTimeout = setTimeout(async () => {
            try {
                setSearchingUsers(true);
                const data = await adminApi.searchUsers(userSearchQuery);
                // Filter out existing managers
                const existingUserIds = managers.map((m) => m.user);
                const filteredUsers = (data.users || []).filter(
                    (u) => !existingUserIds.includes(u.firebaseUID)
                );
                setUserSearchResults(filteredUsers);
            } catch (err) {
                console.error('User search error:', err);
                setUserSearchResults([]);
            } finally {
                setSearchingUsers(false);
            }
        }, 300);

        return () => clearTimeout(searchTimeout);
    }, [userSearchQuery, adminApi, managers]);

    const openAddModal = () => {
        setUserSearchQuery('');
        setUserSearchResults([]);
        setSelectedUser(null);
        setNewManagerRole('admin');
        setShowAddModal(true);
    };

    const handleAddManager = async () => {
        if (!selectedUser) return;

        try {
            setSubmitting(true);
            await orgApi.addManager(organizationId, {
                userId: selectedUser.firebaseUID,
                permissionRole: newManagerRole,
            });
            setSuccess(
                `${selectedUser.name?.first || selectedUser.email} added as ${newManagerRole}`
            );
            setShowAddModal(false);
            loadManagers();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to add manager');
        } finally {
            setSubmitting(false);
        }
    };

    const openRemoveModal = (manager) => {
        setManagerToRemove(manager);
        setShowRemoveModal(true);
    };

    const handleRemoveManager = async () => {
        if (!managerToRemove) return;

        try {
            setSubmitting(true);
            const removedSelf = managerToRemove.user === currentUserId;
            await orgApi.removeManager(organizationId, managerToRemove.user);
            setShowRemoveModal(false);
            setManagerToRemove(null);

            // Losing your own grant does not necessarily end your authority
            // here: the server also accepts `createdBy` and an ecosystem-admin
            // membership, so an org creator or an ecosystem admin stepping down
            // from an explicit grant still reads the list fine. Ask the server
            // rather than guessing from the props this component was handed. A
            // 403 is the definitive answer that it can no longer read what it
            // would refresh, and only then does the caller need to react.
            const { forbidden } = await loadManagers();
            if (removedSelf && forbidden) {
                onSelfRemoved();
                return;
            }

            setSuccess('Manager removed successfully');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to remove manager');
        } finally {
            setSubmitting(false);
        }
    };

    const getRoleBadgeVariant = (role) => {
        switch (role) {
            case 'owner':
                return 'primary';
            case 'admin':
                return 'info';
            default:
                return 'secondary';
        }
    };

    const canRemoveManager = (manager) => {
        // System admins can remove anyone except the last owner
        if (isSystemAdmin) {
            if (manager.permissionRole === 'owner') {
                const ownerCount = managers.filter(
                    (m) => m.permissionRole === 'owner'
                ).length;
                return ownerCount > 1;
            }
            return true;
        }

        // Owners can remove other managers (but not themselves if they're the last owner)
        if (currentUserPermissionRole === 'owner') {
            if (manager.user === currentUserId) {
                const ownerCount = managers.filter(
                    (m) => m.permissionRole === 'owner'
                ).length;
                return ownerCount > 1;
            }
            return true;
        }

        // An admin may revoke their OWN grant and nobody else's: every
        // remaining grant is an owner or an admin, and an admin may remove
        // neither of those from someone else. Without the self case an admin
        // would have no way to step down. Mirrors the server's removeManager
        // guards.
        if (currentUserPermissionRole === 'admin') {
            return manager.user === currentUserId;
        }

        return false;
    };

    if (loading) {
        return (
            <Card className="border-0 shadow-sm">
                <Card.Body className="text-center py-4">
                    <Spinner animation="border" variant="primary" size="sm" />
                </Card.Body>
            </Card>
        );
    }

    return (
        <>
            <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 text-primary">Manage Managers</h5>
                    <Button variant="primary" size="sm" onClick={openAddModal}>
                        Add Manager
                    </Button>
                </Card.Header>
                <Card.Body className="p-0">
                    {error && (
                        <Alert
                            variant="danger"
                            className="m-3"
                            dismissible
                            onClose={() => setError('')}
                        >
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert
                            variant="success"
                            className="m-3"
                            dismissible
                            onClose={() => setSuccess('')}
                        >
                            {success}
                        </Alert>
                    )}

                    {managers.length === 0 ? (
                        <div className="p-4 text-center text-muted">
                            No managers found
                        </div>
                    ) : (
                        <ListGroup variant="flush">
                            {managers.map((manager) => (
                                <ListGroup.Item
                                    key={manager.user}
                                    className="d-flex align-items-center justify-content-between py-3"
                                >
                                    <div className="d-flex align-items-center">
                                        <Image
                                            src={
                                                manager.userProfilePicture ||
                                                DEFAULT_AVATAR
                                            }
                                            alt={
                                                manager.userName ||
                                                manager.userEmail
                                            }
                                            roundedCircle
                                            className="profile-thumbnail-sm me-3"
                                        />
                                        <div>
                                            <div className="fw-semibold">
                                                {manager.userName ||
                                                    manager.userEmail ||
                                                    manager.user}
                                                {manager.user ===
                                                    currentUserId && (
                                                    <span className="text-muted ms-2">
                                                        (You)
                                                    </span>
                                                )}
                                            </div>
                                            {manager.userEmail && (
                                                <small className="text-muted">
                                                    {manager.userEmail}
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <Badge
                                            bg={getRoleBadgeVariant(
                                                manager.permissionRole
                                            )}
                                        >
                                            {manager.permissionRole}
                                        </Badge>
                                        {canRemoveManager(manager) && (
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() =>
                                                    openRemoveModal(manager)
                                                }
                                            >
                                                Remove
                                            </Button>
                                        )}
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </Card.Body>
            </Card>

            {/* Add Manager Modal */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Add Manager</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Search Users</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Search by name or email..."
                            value={userSearchQuery}
                            onChange={(e) => setUserSearchQuery(e.target.value)}
                        />
                        <Form.Text className="text-muted">
                            Type at least 2 characters to search
                        </Form.Text>
                    </Form.Group>

                    {searchingUsers && (
                        <div className="text-center py-2">
                            <Spinner
                                animation="border"
                                size="sm"
                                variant="primary"
                            />
                        </div>
                    )}

                    {userSearchResults.length > 0 && (
                        <ListGroup className="mb-3">
                            {userSearchResults.map((user) => (
                                <ListGroup.Item
                                    key={user._id}
                                    action
                                    active={selectedUser?._id === user._id}
                                    onClick={() => setSelectedUser(user)}
                                    className="d-flex align-items-center"
                                >
                                    <Image
                                        src={
                                            user.profilePicture ||
                                            DEFAULT_AVATAR
                                        }
                                        alt={`${user.name?.first || ''} ${user.name?.last || ''}`}
                                        roundedCircle
                                        className="profile-thumbnail-xs me-2"
                                    />
                                    <div>
                                        <strong>
                                            {user.name?.first} {user.name?.last}
                                        </strong>
                                        <br />
                                        <small className="text-muted">
                                            {user.email}
                                        </small>
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}

                    {userSearchQuery.length >= 2 &&
                        !searchingUsers &&
                        userSearchResults.length === 0 && (
                            <Alert variant="info">
                                No users found matching &quot;{userSearchQuery}
                                &quot;
                            </Alert>
                        )}

                    {selectedUser && (
                        <Card className="mb-3 bg-light">
                            <Card.Body className="py-2">
                                <small className="text-muted">
                                    Selected User:
                                </small>
                                <div className="d-flex align-items-center mt-1">
                                    <Image
                                        src={
                                            selectedUser.profilePicture ||
                                            DEFAULT_AVATAR
                                        }
                                        alt={`${selectedUser.name?.first || ''} ${selectedUser.name?.last || ''}`}
                                        roundedCircle
                                        className="profile-thumbnail-xs me-2"
                                    />
                                    <div>
                                        <strong>
                                            {selectedUser.name?.first || ''}{' '}
                                            {selectedUser.name?.last || ''}
                                        </strong>
                                        <br />
                                        <small>{selectedUser.email}</small>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Role</Form.Label>
                        <Form.Select
                            value={newManagerRole}
                            onChange={(e) => setNewManagerRole(e.target.value)}
                        >
                            <option value="admin">
                                Admin - Can edit the organization and manage
                                other managers
                            </option>
                            {isOwner && (
                                <option value="owner">
                                    Owner - Full control and can manage all
                                    managers
                                </option>
                            )}
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowAddModal(false)}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAddManager}
                        disabled={submitting || !selectedUser}
                    >
                        {submitting ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            'Add Manager'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Remove Manager Modal */}
            <Modal
                show={showRemoveModal}
                onHide={() => setShowRemoveModal(false)}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Remove Manager</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {managerToRemove && (
                        <p>
                            Are you sure you want to remove{' '}
                            <strong>
                                {managerToRemove.userName ||
                                    managerToRemove.userEmail ||
                                    managerToRemove.user}
                            </strong>{' '}
                            from this organization?
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowRemoveModal(false)}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleRemoveManager}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            'Remove'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

ManageManagers.propTypes = {
    organizationId: PropTypes.string.isRequired,
    currentUserId: PropTypes.string.isRequired,
    currentUserPermissionRole: PropTypes.oneOf(['owner', 'admin']).isRequired,
    isSystemAdmin: PropTypes.bool,
    // Called instead of a reload when the current user revokes their own
    // grant, since the component can no longer read the list it would refresh.
    onSelfRemoved: PropTypes.func,
};

ManageManagers.defaultProps = {
    isSystemAdmin: false,
    onSelfRemoved: () => {},
};
